import { RadioGroup, RadioGroupItem } from "components/ui/RadioGroup";
import classNames from "components/ui/classNames";
import aspectRatio from "components/utils/aspectRatio";
import type { SVGProps } from "react";
import { useIntl } from "react-intl";

interface BinaryOperatorRadioGroupProps {
  value: "AND" | "OR";
  disabled?: boolean;
  onChange: (value: "AND" | "OR") => void;
}
export default function BinaryOperatorRadioGroup({
  value = "AND",
  disabled = false,
  onChange,
}: BinaryOperatorRadioGroupProps) {
  const intl = useIntl();
  const iconClassNames = classNames(
    "inline-flex fill-neutral-100 stroke-slate-300 text-slate-600",
    "group-aria-checked:fill-sea-100 group-aria-checked:stroke-sea-300 group-aria-checked:text-sea-600",
    "group-aria-checked:hover:fill-sea-200 group-aria-checked:hover:stroke-sea-400",
    "hover:fill-violet-100 hover:stroke-violet-400/40 hover:text-violet-700",
    "group-aria-checked:pointer-events-none",

    "dark:fill-slate-600 dark:stroke-slate-950 dark:text-white",
    "dark:hover:fill-violet-300/20 dark:hover:stroke-violet-300/70 dark:hover:text-violet-300",
    "dark:group-aria-checked:fill-teal-700/40 dark:group-aria-checked:stroke-teal-300/60 dark:group-aria-checked:text-teal-200",

    "group-disabled:pointer-events-none",
  );
  const icons = intl.locale.startsWith("pt")
    ? [
        {
          operator: "AND",
          tooltipContent: intl.formatMessage({ defaultMessage: "Todas as condições são verdadeiras", id: "m9v+Iv" }),
          icon: (
            <AmpersandIcon
              strokeWidth={2}
              className={iconClassNames}
              {...aspectRatio({ originalWidth: 44, originalHeight: 44, desiredWidth: 32 })}
            />
          ),
        },
        {
          operator: "OR",
          tooltipContent: intl.formatMessage({ defaultMessage: "Ao menos uma condição é verdadeira", id: "DYKX3g" }),
          icon: (
            <OuIcon
              strokeWidth={2}
              className={iconClassNames}
              {...aspectRatio({ originalWidth: 44, originalHeight: 44, desiredWidth: 32 })}
            />
          ),
        },
      ]
    : [
        {
          operator: "AND",
          tooltipContent: intl.formatMessage({ defaultMessage: "Todas as condições são verdadeiras", id: "m9v+Iv" }),
          icon: (
            <AndIcon
              strokeWidth={2}
              className={iconClassNames}
              {...aspectRatio({ originalWidth: 44, originalHeight: 44, desiredWidth: 32 })}
            />
          ),
        },
        {
          operator: "OR",
          tooltipContent: intl.formatMessage({ defaultMessage: "Ao menos uma condição é verdadeira", id: "DYKX3g" }),
          icon: (
            <OrIcon
              strokeWidth={2}
              className={iconClassNames}
              {...aspectRatio({ originalWidth: 44, originalHeight: 44, desiredWidth: 32 })}
            />
          ),
        },
      ];
  return (
    <RadioGroup
      loop={false}
      disabled={disabled}
      className={classNames(
        "inline-flex items-center gap-x-1.5 rounded-2xl border border-transparent bg-transparent",
        "disabled:pointer-events-none",
        "disabled:cursor-default",
        "h-8",
      )}
      orientation="horizontal"
      value={value}
      onValueChange={(value) => onChange(value as "AND" | "OR")}
    >
      {icons.map(({ operator, tooltipContent, icon }) => {
        return (
          <RadioGroupItem
            key={operator}
            value={operator}
            className={classNames("group aria-checked:cursor-default")}
            title={tooltipContent}
          >
            {icon}
          </RadioGroupItem>
        );
      })}
    </RadioGroup>
  );
}

const AmpersandIcon = ({ strokeWidth = 2, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={44}
    height={44}
    {...props}
    viewBox="0 0 44 44"
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    <path
      strokeWidth={(Number(strokeWidth) * 24) / Number(props.width)}
      d="M23.35.5c5.006 0 8.333.809 11.206 2.345a15.863 15.863 0 0 1 6.599 6.6C42.69 12.316 43.5 15.643 43.5 20.65v2.7c0 5.006-.809 8.333-2.345 11.206a15.863 15.863 0 0 1-6.6 6.599C31.684 42.69 28.357 43.5 23.35 43.5h-2.7c-5.006 0-8.333-.809-11.206-2.345a15.863 15.863 0 0 1-6.599-6.6C1.31 31.684.5 28.357.5 23.35v-2.7c0-5.006.809-8.333 2.345-11.206a15.863 15.863 0 0 1 6.6-6.599C12.316 1.31 15.643.5 20.65.5h2.7Z"
    />
    <path
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={0.5}
      d="M25.912 25.104c.128.139.248.227.36.264.112.037.264.056.456.056h1.136v1.872h-1.488c-.459 0-.867-.061-1.224-.184a2.438 2.438 0 0 1-.968-.632l-.192-.192c-.459.373-.928.65-1.408.832a4.449 4.449 0 0 1-1.584.272c-1.173 0-2.133-.259-2.88-.776-.747-.517-1.12-1.261-1.12-2.232 0-.608.179-1.184.536-1.728.357-.544.957-1.003 1.8-1.376-.352-.437-.587-.824-.704-1.16-.117-.336-.176-.75-.176-1.24 0-.544.15-1.035.448-1.472.299-.437.715-.781 1.248-1.032.533-.25 1.136-.376 1.808-.376.576 0 1.104.115 1.584.344.48.23.859.544 1.136.944.277.4.416.85.416 1.352 0 .597-.187 1.133-.56 1.608-.373.475-1.035.91-1.984 1.304l.032.032 1.776 1.888 1.408-2.224h2.128l-2.352 3.472.368.384Zm-4.032-7.52c-.352 0-.643.117-.872.352-.23.235-.344.528-.344.88 0 .31.059.573.176.792.117.219.32.488.608.808.533-.235.92-.493 1.16-.776.24-.283.36-.6.36-.952 0-.33-.101-.597-.304-.8-.203-.203-.464-.304-.784-.304Zm-.736 8.064c.587 0 1.157-.192 1.712-.576L20.344 22.4c-.363.224-.621.475-.776.752a1.938 1.938 0 0 0-.232.96c0 .49.163.87.488 1.136.325.267.765.4 1.32.4Z"
    />
  </svg>
);

const OuIcon = ({ strokeWidth = 2, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={44}
    height={44}
    {...props}
    viewBox="0 0 44 44"
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    <path
      strokeWidth={(Number(strokeWidth) * 24) / Number(props.width)}
      d="M23.35.5c5.006 0 8.333.809 11.206 2.345a15.863 15.863 0 0 1 6.599 6.6C42.69 12.316 43.5 15.643 43.5 20.65v2.7c0 5.006-.809 8.333-2.345 11.206a15.863 15.863 0 0 1-6.6 6.599C31.684 42.69 28.357 43.5 23.35 43.5h-2.7c-5.006 0-8.333-.809-11.206-2.345a15.863 15.863 0 0 1-6.599-6.6C1.31 31.684.5 28.357.5 23.35v-2.7c0-5.006.809-8.333 2.345-11.206a15.863 15.863 0 0 1 6.6-6.599C12.316 1.31 15.643.5 20.65.5h2.7Z"
    />
    <path
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={0.5}
      d="M22.104 21.696c0 1.077-.235 2.05-.704 2.92a5.138 5.138 0 0 1-1.968 2.04c-.843.49-1.803.736-2.88.736s-2.037-.245-2.88-.736a5.138 5.138 0 0 1-1.968-2.04c-.47-.87-.704-1.843-.704-2.92s.235-2.05.704-2.92a5.138 5.138 0 0 1 1.968-2.04c.843-.49 1.803-.736 2.88-.736s2.037.245 2.88.736a5.138 5.138 0 0 1 1.968 2.04c.47.87.704 1.843.704 2.92Zm-8.656 0c0 .704.128 1.333.384 1.888.256.555.619.99 1.088 1.304.47.315 1.013.472 1.632.472.608 0 1.147-.157 1.616-.472.47-.315.832-.75 1.088-1.304.256-.555.384-1.184.384-1.888s-.128-1.333-.384-1.888a3.086 3.086 0 0 0-1.088-1.304 2.834 2.834 0 0 0-1.616-.472c-.619 0-1.163.157-1.632.472-.47.315-.832.75-1.088 1.304-.256.555-.384 1.184-.384 1.888Zm12.432-5.6v6.496c0 .907.176 1.59.528 2.048.352.459.907.688 1.664.688.757 0 1.315-.23 1.672-.688.357-.459.536-1.141.536-2.048v-6.496h2.4v6.368c0 1.6-.392 2.821-1.176 3.664-.784.843-1.928 1.264-3.432 1.264s-2.645-.421-3.424-1.264c-.779-.843-1.168-2.064-1.168-3.664v-6.368h2.4Z"
    />
  </svg>
);

const AndIcon = ({ strokeWidth = 2, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={44}
    height={44}
    {...props}
    viewBox="0 0 44 44"
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    <path
      strokeWidth={(Number(strokeWidth) * 24) / Number(props.width)}
      d="M23.35.5c5.006 0 8.333.809 11.206 2.345a15.863 15.863 0 0 1 6.599 6.6C42.69 12.316 43.5 15.643 43.5 20.65v2.7c0 5.006-.809 8.333-2.345 11.206a15.863 15.863 0 0 1-6.6 6.599C31.684 42.69 28.357 43.5 23.35 43.5h-2.7c-5.006 0-8.333-.809-11.206-2.345a15.863 15.863 0 0 1-6.599-6.6C1.31 31.684.5 28.357.5 23.35v-2.7c0-5.006.809-8.333 2.345-11.206a15.863 15.863 0 0 1 6.6-6.599C12.316 1.31 15.643.5 20.65.5h2.7Z"
    />
    <path
      stroke="none"
      fill="currentColor"
      d="M6 27.2 10.121 16h2.898l4.088 11.2h-2.591l-.87-2.448H9.364L8.495 27.2H6Zm4.008-4.384h2.978L11.49 18.56l-1.48 4.256ZM18.138 27.2V16h2.189l4.781 7.056V16h2.382v11.2h-2.205l-4.781-7.056V27.2zM29.406 16h3.912c1.126 0 2.122.237 2.986.712a4.95 4.95 0 0 1 1.996 1.984c.467.848.7 1.816.7 2.904 0 1.088-.233 2.056-.7 2.904a4.95 4.95 0 0 1-1.996 1.984c-.864.475-1.86.712-2.986.712h-3.912V16Zm3.912 9.2c1.02 0 1.81-.328 2.374-.984.563-.656.845-1.528.845-2.616s-.282-1.96-.845-2.616c-.563-.656-1.355-.984-2.374-.984H31.82v7.2h1.498Z"
    />
  </svg>
);

const OrIcon = ({ strokeWidth = 2, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={44}
    height={44}
    {...props}
    viewBox="0 0 44 44"
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    <path
      strokeWidth={(Number(strokeWidth) * 24) / Number(props.width)}
      d="M23.35.5c5.006 0 8.333.809 11.206 2.345a15.863 15.863 0 0 1 6.599 6.6C42.69 12.316 43.5 15.643 43.5 20.65v2.7c0 5.006-.809 8.333-2.345 11.206a15.863 15.863 0 0 1-6.6 6.599C31.684 42.69 28.357 43.5 23.35 43.5h-2.7c-5.006 0-8.333-.809-11.206-2.345a15.863 15.863 0 0 1-6.599-6.6C1.31 31.684.5 28.357.5 23.35v-2.7c0-5.006.809-8.333 2.345-11.206a15.863 15.863 0 0 1 6.6-6.599C12.316 1.31 15.643.5 20.65.5h2.7Z"
    />
    <path
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={0.5}
      d="M22.104 21.696c0 1.077-.235 2.05-.704 2.92a5.138 5.138 0 0 1-1.968 2.04c-.843.49-1.803.736-2.88.736s-2.037-.245-2.88-.736a5.138 5.138 0 0 1-1.968-2.04c-.47-.87-.704-1.843-.704-2.92s.235-2.05.704-2.92a5.138 5.138 0 0 1 1.968-2.04c.843-.49 1.803-.736 2.88-.736s2.037.245 2.88.736a5.138 5.138 0 0 1 1.968 2.04c.47.87.704 1.843.704 2.92Zm-8.656 0c0 .704.128 1.333.384 1.888.256.555.619.99 1.088 1.304.47.315 1.013.472 1.632.472.608 0 1.147-.157 1.616-.472.47-.315.832-.75 1.088-1.304.256-.555.384-1.184.384-1.888s-.128-1.333-.384-1.888a3.086 3.086 0 0 0-1.088-1.304 2.834 2.834 0 0 0-1.616-.472c-.619 0-1.163.157-1.632.472-.47.315-.832.75-1.088 1.304-.256.555-.384 1.184-.384 1.888Zm14.672-5.6c.81 0 1.528.152 2.152.456a3.373 3.373 0 0 1 1.448 1.304c.341.565.512 1.221.512 1.968 0 .715-.17 1.347-.512 1.896-.341.55-.81.968-1.408 1.256l2.208 4.32h-2.656l-1.872-3.76H25.96v3.76h-2.4v-11.2h4.56Zm-2.16 5.44h1.968c.565 0 1.016-.155 1.352-.464.336-.31.504-.725.504-1.248 0-.533-.168-.955-.504-1.264-.336-.31-.787-.464-1.352-.464H25.96v3.44Z"
    />
  </svg>
);
